import { useContext, useState, useEffect, useRef, ReactElement } from 'react';
import Base from '../../../Base/Base';
import { Button, ButtonGroup } from '@upmc/ims.component-library';

import MaterialIcon from '@material/react-material-icon';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import {
	Actions,
} from '../../../../components/UI/MDC/Card/Card';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import { Subtitle1 } from '../../../../components/UI/MDC/Typography/Typography';
import { DataTable } from '../../../../components/UI/MDC/DataTable/DataTable';
import { AddOrRemoveWorkQueueAdmins } from './WorkQueueAdmin_api';
import { ACTION_TYPES } from '../../../../components/UI/MDC/DataTable/_types';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { init, setRows } from '../../../../store/adminTable/actions';
import { UserContext } from 'context/User/UserContext';
import AdminSearch from './AdminSearch';
import { nanoid } from 'nanoid';

import classes from './WorkQueueAdmin.module.scss';
import { generateRowData } from './_utility';
import { HeaderDataCorrelation } from './_constants';
import { deepEqualObject } from '../../../../store/utility';

/**
 * Wrapping component for Work Queue Admin Table
 *
 * @returns {ReactElement} rendered component.
 */

const AdminTable = ({ setOpenAdminModal, currentSystem }) => {
	//#region REDUX STORE VARIABLES
	const rHeaders = useSelector(state => state.workQueueAdminTable.headers);
	const rActions = useSelector(state => state.workQueueAdminTable.actions);
	const rRows = useSelector(state => state.workQueueAdminTable.rows);
	//#endregion REDUX STORE VARIABLES

	const [currentAdmins, setCurrentAdmins] = useState([...rRows]);
	const [adminsToDelete, setAdminsToDelete] = useState();
	const [dismissModal, setDismissModal] = useState(false);
	const [different, setDifferent] = useState(false);

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const dispatch = useDispatch();

	// Needed to maintain reference to the currentAdmins
	const currentAdminRef = useRef();
	currentAdminRef.current = currentAdmins;

	// Reference to current row and the modal
	const rowToUse = useRef();
	const modalRef = useRef(null);

	/**
	 * Scrolls to top of modal ref
	 *
	 * @returns void
	 */
	const scrollToTop = () => {
		modalRef.current.scroll({ top: 0, left: 0, behavior: 'smooth' });
	};

	// Maintains the admin ref with the current admins list
	useEffect(() => {
		currentAdminRef.current = [...currentAdmins];
	}, [currentAdmins]);

	/**
	 * Removes selected row from admin list
	 * *@param row is the current row in the admin list when clicking the delete action
	 * @returns void
	 */
	const removeCurrentAdmin = row => {
		rowToUse.current = row;
		setCurrentAdmins(prevAdmins =>
			prevAdmins.filter(
				obj => obj.Cells['FI_ID'].Value !== row.Cells['FI_ID'].Value
			)
		);
	};
	// Initial creation Redux Store
	useEffect(() => {
		dispatch(
			init({
				actions: [
					{
						Type: ACTION_TYPES.REMOVE,
						onChangedHandler: (_, __, row) => {
							removeCurrentAdmin(row);
						},
						Header: {
							Checked: false,
							Key: '0',
							onChangedHandler: e => {
								setCurrentAdmins([]);
								setAdminsToDelete(rRows);
							},
						},
					},
				],
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// add/remove admins to list for API
	useEffect(() => {
		if (rowToUse.current !== undefined) {
			var index = adminsToDelete?.findIndex(
				x =>
					x.Cells['FI_ID'].Value ===
					rowToUse.current.Cells['FI_ID'].Value
			);
			setAdminsToDelete(
				adminsToDelete
					? index === -1
						? [...adminsToDelete, rowToUse.current]
						: [
								...adminsToDelete.filter(
									x =>
										x.Cells['FI_ID'].Value !==
										rowToUse.current.Cells['FI_ID'].Value
								),
						  ]
					: [rowToUse.current]
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rowToUse.current]);

	/**
	 * Toggles between adding or removing an admin to the list of admins
	 *
	 * @returns void
	 */
	const toggleAddAdmin = row => {
		rowToUse.current = row;
		var newRow = {
			ActionCells: [
				{
					BindTo: 0,
					Checked: false,
					Key: nanoid(),
					Text: 'Delete',
					Type: ACTION_TYPES.REMOVE,
				},
			],
			Cells: {
				'Account Admin': {
					Value: row.Cells.Name.Value,
					Validation: {
						Key: 'true',
						Value: null,
						memberToValidateKey: null,
					},
				},
				'Applications to Process': {
					Value: '0',
					Validation: {
						Key: 'true',
						Value: null,
						memberToValidateKey: null,
					},
				},
				FI_ID: {
					Value: row.Cells.FI_ID.Value,
					Validation: {
						Key: 'true',
						Value: null,
						memberToValidateKey: null,
					},
				},
				'Oldest Request Pending': {
					Value: 'n/a',
					Validation: {
						Key: 'true',
						Value: null,
						memberToValidateKey: null,
					},
				},
				'Requests to Process': {
					Value: '0',
					Validation: {
						Key: 'true',
						Value: null,
						memberToValidateKey: null,
					},
				},
			},
			isDisplayed: true,
			Key: nanoid(),
			props: { selected: true },
		};
		setCurrentAdmins([newRow, ...currentAdminRef.current]);
		scrollToTop();
	};

	useEffect(() => {
		if (deepEqualObject(rRows, currentAdmins) === false) {
			setDifferent(true);
		} else {
			setDifferent(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentAdmins]);

	/**
	 * Renders the DataTable component for work queue admins
	 *
	 * @returns {ReactElement} Work Queue Admins DataTale component
	 */
	const WorkQueueAdmins = () => {
		return (
			<DataTable
				rows={currentAdmins}
				actions={rActions}
				headers={rHeaders}
				label='Current Work Queue Admins'
			/>
		);
	};

	/**
	 * Saves work queue admins to database via API
	 *
	 * @returns void
	 */
	const saveWorkQueueAdmins = () => {
		var dataToSend = {
			User_ESO_ID: user.ESOID,
			WQ_System_ID: currentSystem.ID.toString(),
			Users: [
				...adminsToDelete.map(row => ({
					ID: Number(row.Cells['FI_ID'].Value),
					Name:
						row.Cells['Name']?.Value ??
						row.Cells['Account Admin']?.Value,
				})),
			],
		};

		AddOrRemoveWorkQueueAdmins(dataToSend).then(resp => {
			var data = resp?.data?.payload?.Results;
			if (data?.length > 0) {
				var rows = generateRowData(data, HeaderDataCorrelation);
				dispatch(setRows(rows));
			}
		});
	};
	return (
		<div ref={modalRef} className={classes.Modal}>
			<Base title='Add or Remove Work Queue Admins'>
				<Dialog
					open={dismissModal}
					onClose={() => {
						setOpenAdminModal(false);
						setDismissModal(false);
					}}
				>
					<DialogTitle sx={{ fontFamily: 'inherit' }}>
						Unsaved Changes
					</DialogTitle>
					<DialogContent>
						Do you want to save the information before leaving?
					</DialogContent>
					<DialogActions>
						<Button
							variant='text'
							onClick={() => {
								setOpenAdminModal(false);
								setDismissModal(false);
							}}
						>
							Exit
						</Button>
						<Button
							variant='text'
							onClick={() => {
								setOpenAdminModal(false);
								setDismissModal(false);
								saveWorkQueueAdmins();
							}}
						>
							Save
						</Button>
					</DialogActions>
				</Dialog>
				<div style={{ width: 'inherit', marginBottom: 10 }}>
					<WorkQueueAdmins />
				</div>

				<StyleAuxiliary classes={classes.body__container}>
					<AdminSearch
						toggleAddAdmin={row => toggleAddAdmin(row)}
						currentAdmins={currentAdmins}
					/>
				</StyleAuxiliary>

				<StyleAuxiliary classes={classes.controls}>
					<Actions>
						<ButtonGroup>
							<Button
								startIcon={<MaterialIcon icon='cancel' />}
								variant='outlined'
								onClick={() => {
									if (different) {
										setDismissModal(true);
									} else {
										setOpenAdminModal(false);
									}
								}}
								id='FavUpdateCancel-Top'
							>
								Cancel
							</Button>
							<Button
								variant='contained'
								startIcon={<MaterialIcon icon='save' />}
								disabled={!different}
								onClick={() => {
									setOpenAdminModal(false);
									saveWorkQueueAdmins();
								}}
								id='FavUpdateSave-Top'
							>
								Save
							</Button>
						</ButtonGroup>
					</Actions>
					<Subtitle1 className={classes['subtitle1--warning--bold']}>
						* Adding/removing admins might take several minutes to
						complete
					</Subtitle1>
				</StyleAuxiliary>
			</Base>
		</div>
	);
};

export const AdminTablePropTypes = {
	setOpenAdminModal: PropTypes.func,
	currentSystem: PropTypes.any,
};

export default AdminTable;
